// hooks/useFeedback.ts — reactive hooks for the product feedback board.
//
// Components must NOT import feedbackStore or feedbackService directly.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useFeedbackStore } from '@/core/stores/feedbackStore';
import type { FeedbackLoadState } from '@/core/stores/feedbackStore';
import { feedbackService } from '@/core/services/feedbackService';
import type { FeedbackQuery } from '@/core/services/feedbackService';
import type {
  CreateFeedbackInput,
  FeedbackItem,
  FeedbackListItem,
  FeedbackManageInput,
  FeedbackRoadmapInput,
  FeedbackStageInput,
  FeedbackStats,
  FeedbackStatus,
} from '@/core/models/feedback';

export interface FeedbackListHook {
  items: FeedbackListItem[];
  /** 'loading' until the first fetch settles — distinguishes empty from pending. */
  loadState: FeedbackLoadState;
  /**
   * Creates the item ONLY — no filter-scope reconciliation (see
   * reconcileAfterCreate). Split out (2026-08-10 adversarial review, finding
   * F2) so a caller that also uploads attachments can do so BETWEEN create
   * and reconcile: feedbackService.uploadAttachment unconditionally
   * upsert()s its response into the store, and running the filter-scope
   * reload before those uploads finish let an upload's upsert silently
   * resurrect an item the reload had just correctly removed.
   */
  submit: (input: CreateFeedbackInput) => Promise<FeedbackItem>;
  /**
   * Runs AFTER submit (and, if the caller attaches files, after every
   * attachment upload has settled) — reconciles the store back to server
   * truth for the active filter when the newly-created item doesn't match
   * it (2026-08-10 adversarial review, item 16's original fix, now split out
   * as finding F2's fix: this must be the LAST write for a given create, or
   * a later attachment-upload upsert can undo it).
   */
  reconcileAfterCreate: (item: FeedbackItem) => Promise<void>;
  toggleUpvote: (id: string) => Promise<void>;
  /** Staff-only status transition, addressed by row id — used by the Owner tracker table. */
  setStatus: (id: string, status: FeedbackStatus) => Promise<void>;
  /** Staff-only hide/unhide, addressed by row id — used by the Owner tracker table. */
  toggleRemoved: (id: string) => Promise<void>;
  reload: () => Promise<void>;
}

/**
 * The board list. Refetches whenever the query changes — the payload carries the
 * viewer-specific `upvoted` flag, so it must not be cached across sessions.
 */
export function useFeedbackList(query: FeedbackQuery = {}): FeedbackListHook {
  const items = useFeedbackStore((s) => s.items);
  const loadState = useFeedbackStore((s) => s.loadState);

  // Serialise the query so the effect key is stable across object identities.
  const key = JSON.stringify(query);

  const reload = useCallback(async () => {
    await feedbackService.list(JSON.parse(key) as FeedbackQuery).catch(() => {});
  }, [key]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return {
    items,
    loadState,
    reload,
    submit: async (input) => {
      // create() ONLY — see FeedbackListHook.submit's docstring for why the
      // filter-scope reconciliation is a separate step (F2) the caller runs
      // after any attachment uploads, not inline here.
      return feedbackService.create(input);
    },
    reconcileAfterCreate: async (item) => {
      // feedbackService.create() (and feedbackService.uploadAttachment, for a
      // caller that attaches files) unconditionally upsert the item into the
      // store's item array, but that array IS the board's active-filter-
      // scoped visible list here — an item that does not match the current
      // category/status filter must never appear until the viewer actually
      // switches to a filter it matches. Refetching resets the store back to
      // server truth for the active query, undoing the blind upsert when it
      // doesn't belong (2026-08-10 adversarial review, item 16); when it DOES
      // match, this refetch is a harmless no-op (the upserted item was
      // already correct).
      const q = JSON.parse(key) as FeedbackQuery;
      const matchesCategory = !q.category || q.category === item.category;
      const matchesStatus = !q.status || q.status === item.status;
      if (!matchesCategory || !matchesStatus) {
        await feedbackService.list(q).catch(() => {});
      }
    },
    toggleUpvote: async (id) => {
      await feedbackService.toggleUpvote(id);
    },
    setStatus: async (id, status) => {
      await feedbackService.setStatus(id, status);
    },
    toggleRemoved: async (id) => {
      await feedbackService.toggleRemoved(id);
    },
  };
}

/**
 * A one-off list fetch that does not touch the shared board store — for rail
 * panels (trending / roadmap preview) that must not clobber the main board's
 * loadState while it mounts alongside FeedbackPage.
 */
export function useFeedbackPeek(query: FeedbackQuery = {}): {
  items: FeedbackListItem[];
  loading: boolean;
  error: boolean;
  reload: () => void;
} {
  const key = JSON.stringify(query);
  const [items, setItems] = useState<FeedbackListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    // Refires on every query change (or an explicit reload()), so it must
    // reset loading/error itself — the initial state alone only covers the
    // first run.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional loader flags
    setLoading(true);
    setError(false);
    feedbackService
      .peek(JSON.parse(key) as FeedbackQuery)
      .then((res) => {
        if (alive) setItems(res);
      })
      .catch(() => {
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [key, tick]);

  return { items, loading, error, reload: () => setTick((t) => t + 1) };
}

/** Roadmap bucket keys — 'completed' merges shipped/resolved/closed (the
 * same set the /stats/ tile calls "completed"), since the list endpoint can
 * only filter on one status value at a time. */
export type RoadmapBucketKey = 'planned' | 'in-progress' | 'testing' | 'completed';

export const ROADMAP_BUCKET_LABELS: Record<RoadmapBucketKey, string> = {
  'planned': 'Planned',
  'in-progress': 'In progress',
  'testing': 'Testing',
  'completed': 'Completed',
};

const COMPLETED_FAMILY: FeedbackStatus[] = ['shipped', 'resolved', 'closed'];

export interface RoadmapBucketsHook {
  buckets: Record<RoadmapBucketKey, FeedbackListItem[]>;
  loading: boolean;
  error: boolean;
  reload: () => void;
}

const EMPTY_BUCKETS: Record<RoadmapBucketKey, FeedbackListItem[]> = {
  planned: [],
  'in-progress': [],
  testing: [],
  completed: [],
};

/**
 * Fetches each roadmap column from its OWN status-filtered request (Planned /
 * In progress / Testing / Completed-family) rather than one top-N read across
 * every status — a single top-100 read can under-represent (or entirely miss)
 * a column once the board has more than ~100 items total, even though that
 * column itself has plenty of room (2026-08-10 adversarial review, item 7).
 * `limit` caps each request — pass a small value (e.g. 2) for a rail preview,
 * omit it for the full roadmap page.
 */
export function useFeedbackRoadmapBuckets(limit?: number): RoadmapBucketsHook {
  const [buckets, setBuckets] = useState(EMPTY_BUCKETS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional loader flags, see useFeedbackPeek
    setLoading(true);
    setError(false);
    const q = (status: FeedbackStatus) => feedbackService.peek({ status, sort: 'top', limit });
    Promise.all([q('planned'), q('in-progress'), q('testing'), ...COMPLETED_FAMILY.map(q)])
      .then(([planned, inProgress, testing, shipped, resolved, closed]) => {
        if (!alive) return;
        const completed = [...(shipped ?? []), ...(resolved ?? []), ...(closed ?? [])].sort(
          (a, b) => b.upvotes - a.upvotes,
        );
        setBuckets({ planned: planned ?? [], 'in-progress': inProgress ?? [], testing: testing ?? [], completed });
      })
      .catch(() => {
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [limit, tick]);

  return { buckets, loading, error, reload: () => setTick((t) => t + 1) };
}

export interface FeedbackStatsHook {
  stats: FeedbackStats | null;
  loading: boolean;
  error: boolean;
  reload: () => Promise<void>;
}

export function useFeedbackStats(): FeedbackStatsHook {
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await feedbackService.stats();
      setStats(res);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reload() sets loader flags, see above
    void reload();
  }, [reload]);

  return { stats, loading, error, reload };
}

/** Debounced duplicate-search for the create dialog's "Before you submit" box. */
export function useFeedbackSearch(q: string, delayMs = 300): {
  results: FeedbackListItem[];
  searching: boolean;
} {
  const [results, setResults] = useState<FeedbackListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < 3) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- resets on every keystroke
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const mySeq = ++seq.current;
    const timer = setTimeout(() => {
      feedbackService
        .peek({ q: trimmed, limit: 5 })
        .then((res) => {
          if (seq.current === mySeq) setResults(res);
        })
        .catch(() => {
          if (seq.current === mySeq) setResults([]);
        })
        .finally(() => {
          if (seq.current === mySeq) setSearching(false);
        });
    }, delayMs);
    return () => clearTimeout(timer);
  }, [q, delayMs]);

  return { results, searching };
}

export interface FeedbackItemHook {
  item: FeedbackItem | null;
  /** True until the by-id lookup settles. Render neither the page nor "not found". */
  loading: boolean;
  /** The id genuinely does not exist (404) — distinct from `error` below. */
  notFound: boolean;
  /** The lookup failed for any reason OTHER than "does not exist" (network,
   * 500, etc.) — render an error+retry state, never "not found". */
  error: boolean;
  reload: () => Promise<void>;
  toggleUpvote: () => Promise<void>;
  toggleWatch: () => Promise<void>;
  comment: (body: string, internal?: boolean) => Promise<void>;
  toggleCommentHelpful: (commentId: string | number) => Promise<void>;
  /** Staff-only status transition. */
  setStatus: (status: FeedbackStatus, statusNote?: string) => Promise<void>;
  /** can_manage-gated tracker field edit. */
  manage: (input: FeedbackManageInput) => Promise<void>;
  setRoadmap: (input: FeedbackRoadmapInput) => Promise<void>;
  addStage: (title: string) => Promise<void>;
  updateStage: (stageId: number, input: FeedbackStageInput) => Promise<void>;
  /** Resolves `{refreshed}` — the delete itself either succeeded or this
   * throws; `refreshed: false` means the delete went through but the
   * follow-up refetch failed, so the store may now be stale. */
  deleteStage: (stageId: number) => Promise<{ refreshed: boolean }>;
  reorderStages: (order: number[]) => Promise<void>;
  addRelation: (toId: string) => Promise<void>;
  /** See deleteStage. */
  removeRelation: (toId: string) => Promise<{ refreshed: boolean }>;
  uploadAttachment: (file: File) => Promise<void>;
  /** See deleteStage. */
  removeAttachment: (attachmentId: number) => Promise<{ refreshed: boolean }>;
  toggleRemoved: () => Promise<void>;
  /** can_manage-gated. Idempotent (adding an already-present label is a no-op). */
  addTag: (label: string) => Promise<void>;
  /** can_manage-gated. See deleteStage. */
  removeTag: (label: string) => Promise<{ refreshed: boolean }>;
}

/**
 * Single item for the detail route. Same shape as useCommunity: a store hit
 * renders immediately and refreshes underneath, a miss fetches by id, and
 * `loading` stays true until the fetch promise settles — never a timer, which
 * would flash "not found" for one round-trip on every deep link.
 */
/** True once an item carries the detail-only fields — i.e. it came from a
 * detail fetch (GET /feedback/{id}/), not merely a list-shaped store entry
 * left behind by the board or a rail panel. Guards against rendering
 * `item.roadmap.stages` etc. off a partial object. */
function hasDetail(item: FeedbackListItem | null): item is FeedbackItem {
  return !!item && 'roadmap' in item;
}

export function useFeedbackItem(id: string | null | undefined): FeedbackItemHook {
  const storeItem = useFeedbackStore(
    useShallow((s) => s.items.find((i) => i.id === id) ?? null),
  );
  const item = hasDetail(storeItem) ? storeItem : null;
  const [settledId, setSettledId] = useState<string | null>(null);
  // 'idle' covers both "hasn't run yet" and "currently in flight" — loading
  // below is driven off settledId, not this, so it never needs a distinct
  // 'loading' member of its own.
  const [fetchOutcome, setFetchOutcome] = useState<'idle' | 'ok' | 'not-found' | 'error'>('idle');

  useEffect(() => {
    if (!id) return;
    let alive = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- refires per id, see useFeedbackPeek
    setFetchOutcome('idle');
    void feedbackService
      .get(id)
      .then((res) => {
        if (alive) setFetchOutcome(res.status);
      })
      .finally(() => {
        if (alive) setSettledId(id);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const reload = useCallback(async () => {
    if (!id) return;
    const res = await feedbackService.get(id);
    setFetchOutcome(res.status);
  }, [id]);

  const loading = !!id && settledId !== id;

  return {
    item,
    loading,
    notFound: !loading && fetchOutcome === 'not-found',
    error: !loading && fetchOutcome === 'error',
    reload,
    toggleUpvote: async () => {
      if (id) await feedbackService.toggleUpvote(id);
    },
    toggleWatch: async () => {
      if (id) await feedbackService.toggleWatch(id);
    },
    comment: async (body, internal = false) => {
      if (id) await feedbackService.comment(id, body, internal);
    },
    toggleCommentHelpful: async (commentId) => {
      if (id) await feedbackService.toggleCommentHelpful(id, commentId);
    },
    setStatus: async (status, statusNote) => {
      if (id) await feedbackService.setStatus(id, status, statusNote);
    },
    manage: async (input) => {
      if (id) await feedbackService.manage(id, input);
    },
    setRoadmap: async (input) => {
      if (id) await feedbackService.setRoadmap(id, input);
    },
    addStage: async (title) => {
      if (id) await feedbackService.addStage(id, title);
    },
    updateStage: async (stageId, input) => {
      if (id) await feedbackService.updateStage(id, stageId, input);
    },
    deleteStage: async (stageId) => {
      if (!id) return { refreshed: true };
      return feedbackService.deleteStage(id, stageId);
    },
    reorderStages: async (order) => {
      if (id) await feedbackService.reorderStages(id, order);
    },
    addRelation: async (toId) => {
      if (id) await feedbackService.addRelation(id, toId);
    },
    removeRelation: async (toId) => {
      if (!id) return { refreshed: true };
      return feedbackService.removeRelation(id, toId);
    },
    uploadAttachment: async (file) => {
      if (id) await feedbackService.uploadAttachment(id, file);
    },
    removeAttachment: async (attachmentId) => {
      if (!id) return { refreshed: true };
      return feedbackService.removeAttachment(id, attachmentId);
    },
    toggleRemoved: async () => {
      if (id) await feedbackService.toggleRemoved(id);
    },
    addTag: async (label) => {
      if (id) await feedbackService.addTag(id, label);
    },
    removeTag: async (label) => {
      if (!id) return { refreshed: true };
      return feedbackService.removeTag(id, label);
    },
  };
}
